import "./i18n";
import { CssBaseline } from "@mui/material";
import React from "react";
import ReactDOM from "react-dom";
import * as Sentry from "@sentry/react";
import { BrowserRouter } from "react-router-dom";
import ReactGA from "react-ga4";
import { ThemeProvider } from "styled-components";
import { applyMiddleware, createStore, compose } from "redux";
import { Provider as ReduxProvider } from "react-redux";
// import { createLogger as createReduxLogger } from "redux-logger";
import thunk from "redux-thunk";
import { reducer } from "./store";
import { isSuperCarteiraTheme } from "./utils/domain/ThemeUtils";
import { createAnalyticsMiddleware } from "./analytics/analyticsMiddleware";
import {
  BaseStyles,
  defaultTheme,
  ouiTheme,
  superCarteiraTheme,
  blankLabelTheme,
  ThemeType,
  kurtosysTheme,
  investsetTheme,
} from "./themes";
import App from "./App";
import { isProdMode } from "./utils/domain/EnvUtils";

if (isProdMode() && isSuperCarteiraTheme()) {
  Sentry.init({
    dsn: String(process.env.REACT_APP_SENTRY_DSN),
    environment: String(process.env.REACT_APP_SENTRY_ENVIRONMENT),
    release: String(process.env.REACT_APP_VERSAO_DEPLOY_FOREMAN),

    sendDefaultPii: true,
    integrations: [
      Sentry.replayIntegration(),
      Sentry.browserTracingIntegration(),
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,
  });
}

const selectedTheme = process.env.REACT_APP_THEME || "";
const theme = (): ThemeType => {
  switch (selectedTheme) {
    case "OUI":
      return ouiTheme;
    case "SUPER_CARTEIRA":
      return superCarteiraTheme;
    case "BLANK_LABEL":
      return blankLabelTheme;
    case "KURTOSYS":
      return kurtosysTheme;
    case "INVESTSET":
      return investsetTheme;
    default:
      return defaultTheme;
  }
};

const googleAnalyticsId = process.env.REACT_APP_GOOGLE_ANALYTICS_ID ?? "none";

if (!/none/.test(googleAnalyticsId)) {
  ReactGA.initialize(googleAnalyticsId);
}

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
  }
}

// const loggerMiddleware = createReduxLogger({
//   collapsed: true,
//   duration: true,
//   diff: false,
// });

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const middleware =
  process.env.REACT_APP_USE_REDUX_LOGGER === "true"
    ? composeEnhancers(
        // NOTE: only use when needed for debugging
        // applyMiddleware(loggerMiddleware, thunk),
        applyMiddleware(thunk),
        applyMiddleware(createAnalyticsMiddleware)
      )
    : composeEnhancers(
        applyMiddleware(thunk),
        applyMiddleware(createAnalyticsMiddleware)
      );

const store = createStore(reducer, middleware);
export type AppDispatchType = typeof store.dispatch;
export type RootStateType = ReturnType<typeof store.getState>;
export type CallableStateType = typeof store.getState;

ReactDOM.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary>
      <ReduxProvider store={store}>
        <BrowserRouter>
          <CssBaseline />
          <BaseStyles />
          <ThemeProvider theme={theme}>
            <App />
          </ThemeProvider>
        </BrowserRouter>
      </ReduxProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
  document.getElementById("root")
);
